import { View, Text, ScreenContainer } from "@src/components";
import { sizes } from "@src/constants/sizes";
import { useRouter } from "expo-router";
import { Button } from "@src/components/Button";
import { useUnistyles, StyleSheet } from "react-native-unistyles";
import { Alert } from "react-native";
import { useBiometricAuth } from "@src/hooks/useBiometricAuth";
import { AppStorageKeys, BiometricPreference, useStorage } from "@src/services/app-storage";
import { setSessionUnlocked } from "@src/features/auth/utils";
import BiometricIcon from "@src/components/Biometrics/BiometricIcon";
import { BackRoundedButton } from "@src/components/BackRoundedButton";
import { useMemo, useEffect } from "react";
import { FocusedSystemBars } from "@src/components/FocusedSystemBars";
import { BackgroundGradient } from "@src/components/BackgroundGradient";
import { useAmplitude } from "@src/hooks/useAmplitude";
import { ANALYTICS_EVENTS } from "@src/services/analytics/events";

const BiometricsScreen = () => {
  const router = useRouter();
  const { theme } = useUnistyles();
  const { isAvailable, biometryType, isLoading, authenticate } = useBiometricAuth();
  const { track } = useAmplitude();
  const [, setBiometricPreference] = useStorage<BiometricPreference>(AppStorageKeys.BIOMETRIC_PREFERENCE);

  // Track when biometric setup screen is opened
  useEffect(() => {
    track(ANALYTICS_EVENTS.BIOMETRIC_SETUP_STARTED, {
      is_available: isAvailable,
      biometry_type: biometryType || "unknown",
    });
  }, [isAvailable, biometryType, track]); // Only track on mount

  const title = useMemo(() => {
    if (isAvailable) {
      return `Enable ${biometryType || "biometric"} authentication?`;
    }
    return "Biometrics not available";
  }, [isAvailable, biometryType]);

  const description = useMemo(() => {
    if (isAvailable) {
      return `You'll use ${biometryType || "biometrics"} to sign in quickly. You'll also set up a passcode as a backup option.`;
    }
    return "Your device doesn't support biometric authentication. You'll use a passcode to secure your account.";
  }, [isAvailable, biometryType]);

  const handleEnableBiometrics = async () => {
    if (!isAvailable) {
      Alert.alert("Biometrics Not Available", "Biometric authentication is not available on this device.", [
        { text: "OK", onPress: () => router.push("/auth/pin") },
      ]);
      return;
    }

    const authResult = await authenticate(`Authenticate to enable ${biometryType} for this app`);

    if (!authResult) {
      Alert.alert("Authentication Failed", "Please ensure biometrics or device credentials are set up.", [
        { text: "OK" },
      ]);
      return;
    }

    // Enable biometrics in storage
    setBiometricPreference({ biometricEnabled: true });

    // Mark session as unlocked (user just completed onboarding)
    setSessionUnlocked(true);

    track(ANALYTICS_EVENTS.BIOMETRIC_SETUP_ENABLED, {
      biometry_type: biometryType || "unknown",
    });

    router.replace("/onboarding/push-notifications");
  };

  const handleSkip = () => {
    // Ensure biometrics are disabled when skipped
    setBiometricPreference({ biometricEnabled: false });

    track(ANALYTICS_EVENTS.BIOMETRIC_SETUP_SKIPPED, {
      biometry_type: biometryType || "unknown",
    });

    router.push("/auth/pin");
  };

  return (
    <>
      <FocusedSystemBars style="light" />
      <BackgroundGradient>
        <ScreenContainer
          edges={["top", "bottom"]}
          style={{ backgroundColor: "transparent" }}
          contentMaxWidth={sizes.maxWidthSm}
        >
          {/* Header */}

          {router.canGoBack() ?
            <View style={styles.backButtonContainer}>
              <BackRoundedButton onPress={() => router.back()} />
            </View>
          : <View style={{ height: 56 }} />}

          <View flex={1} px="4xl">
            <View mt="9xl" alignItems="center">
              <Text textStyle="h3/regular" textAlign="center" style={styles.text}>
                {title}
              </Text>
            </View>

            {/* Biometric Icon Placeholder */}
            <View alignItems="center" justifyContent="center" mt="11xl">
              <BiometricIcon biometryType={biometryType || "Biometric"} />
            </View>
            <View mt="11xl" alignItems="center" gap="xl">
              <Text textStyle="sm/regular" textAlign="center" style={styles.text}>
                {description}
              </Text>
            </View>

            {/* Buttons */}
            <View mt="13xl">
              <Button
                label={!isAvailable ? "Biometrics not available" : `Enable ${biometryType || "Biometrics"}`}
                textStyle="md/regular"
                size="md"
                variant="primary"
                variantColor="blue"
                onPress={handleEnableBiometrics}
                disabled={isLoading || !isAvailable}
              />
            </View>
            <View mt="3xl">
              <Button
                label={isAvailable ? "Skip, use passcode only" : "Use passcode only"}
                textStyle="md/regular"
                size="md"
                variant="secondary"
                onPress={handleSkip}
                disabled={isLoading}
                textColor={theme.colors.white.default}
                style={{ borderColor: theme.colors.surface1.foregroundSecondary }}
              />
            </View>
          </View>
        </ScreenContainer>
      </BackgroundGradient>
    </>
  );
};

const styles = StyleSheet.create((theme) => ({
  backButtonContainer: {
    marginLeft: theme.spacing["3xl"],
    marginTop: theme.spacing["3xl"],
  },
  text: {
    color: theme.colors.interactive.buttons.default_foreground,
  },
}));

export default BiometricsScreen;
